import { useRouterState } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { useEffect, useState } from "react";
import { BrandMark } from "../ui/icon";

/** Shown only when moving takes longer than this: a quick move does not flicker. */
export const ROUTE_PENDING_SHOW_MS = 1000;
/** How long it takes to fade away. */
const LEAVE_MS = 200;

/**
 * The "loading" badge while moving to another screen takes long (a slow
 * server): the previous screen stays until the next one's data is there, and
 * without it a click looks as if it did nothing.
 *
 * It comes once the router has been pending for {@link ROUTE_PENDING_SHOW_MS},
 * and fades away when it is done. Only the router's `status` is read (not
 * where it is going). Wide screens: the bottom right corner (the notes'
 * corner). Phones: in the middle, just above the Dock (phone.css).
 * Glass, like the notes. Not pressable.
 */
export function RoutePendingBadge() {
  const pending = useRouterState({ select: (s) => s.status === "pending" });
  const [shown, setShown] = useState(false);
  const [mounted, setMounted] = useState(false);
  // Not a fetch: the wait before showing, and the fade on the way out.
  useEffect(() => {
    if (pending) {
      const timer = setTimeout(() => {
        setMounted(true);
        setShown(true);
      }, ROUTE_PENDING_SHOW_MS);
      return () => clearTimeout(timer);
    }
    setShown(false);
    const timer = setTimeout(() => setMounted(false), LEAVE_MS);
    return () => clearTimeout(timer);
  }, [pending]);

  if (!mounted) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-route-pending=""
      className={`pointer-events-none fixed right-3 bottom-3 z-[56] ${
        shown
          ? "route-pending-in opacity-100"
          : "opacity-0 transition-opacity duration-200 ease-in"
      }`}
    >
      <div
        data-route-pending-face=""
        className="relative isolate inline-flex min-h-8 items-center gap-2 rounded-2xl py-1 pr-[13px] pl-2.5 text-[13px] text-fg leading-[1.4]"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 rounded-[inherit] [transform:translateZ(0)]"
        >
          <span className="glass block size-full rounded-[inherit]" />
        </span>
        <span
          data-route-pending-mark=""
          className="route-pending-spin size-[21px] flex-none [&>svg]:size-full"
        >
          <BrandMark />
        </span>
        <span>{m.shell_route_pending()}</span>
      </div>
    </div>
  );
}
